import { usePage } from '@inertiajs/vue3';
import { computed } from 'vue';
import type { Team } from '@/types';

/**
 * Tiny helper returning team-scoped URL builders driven by the current
 * team slug shared via Inertia props. Keeps URL construction in one place
 * and reads as `team.dashboard()` / `team.studio()` / …
 */
export function useTeamRoutes() {
    const page = usePage();
    const slug = computed<string>(
        () => ((page.props.currentTeam as Team | null | undefined)?.slug ?? ''),
    );

    const prefix = (path: string) => `/${slug.value}${path.startsWith('/') ? '' : '/'}${path}`;

    return {
        slug,
        dashboard: () => prefix('/dashboard'),
        studio: () => prefix('/studio'),
        studioGallery: () => prefix('/studio/gallery'),
        studioModelNew: () => prefix('/studio/models/new'),
        studioModel: (modelSlug: string) => prefix(`/studio/models/${modelSlug}`),
        studioGenerate: (modelSlug: string) => prefix(`/studio/models/${modelSlug}/generate`),
        studioPoll: (modelSlug: string) => prefix(`/studio/models/${modelSlug}/generations`),
        studioFavorite: (generationId: number | string) => prefix(`/studio/generations/${generationId}/favorite`),
        studioDeleteGen: (generationId: number | string) => prefix(`/studio/generations/${generationId}`),
        content: () => prefix('/content'),
        contentGenerate: () => prefix('/content/generate'),
        contentCategoriesStore: () => prefix('/content/categories'),
        contentItemsStore: () => prefix('/content/items'),
        contentItemNew: () => prefix('/content/items/new'),
        contentItem: (contentSlug: string) => prefix(`/content/items/${contentSlug}`),
        contentItemUpdate: (contentSlug: string) => prefix(`/content/items/${contentSlug}`),
        contentItemJob: (contentSlug: string) => prefix(`/content/items/${contentSlug}/jobs`),
        contentJob: (jobId: string) => prefix(`/content/${jobId}`),
        contentJobPoll: (jobId: string) => prefix(`/content/${jobId}/poll`),
        contentPiece: (pieceId: string) => prefix(`/content/pieces/${pieceId}`),
        contentPieceRegenerate: (pieceId: string) => prefix(`/content/pieces/${pieceId}/regenerate`),
        contentPieceFavorite: (pieceId: string) => prefix(`/content/pieces/${pieceId}/favorite`),
        contentPieceDismiss: (pieceId: string) => prefix(`/content/pieces/${pieceId}/dismiss`),
        contentPieceRestore: (pieceId: string) => prefix(`/content/pieces/${pieceId}/restore`),
        contentPieceVersionRestore: (pieceId: string, versionId: string) =>
            prefix(`/content/pieces/${pieceId}/versions/${versionId}/restore`),
        contentPiecesRegenerateMany: () => prefix('/content/pieces/regenerate'),
        contentPiecesDismissMany: () => prefix('/content/pieces/dismiss'),
        scheduler: () => prefix('/scheduler'),
        posts: () => prefix('/posts'),
        postsCreate: (pieceId?: string) =>
            pieceId
                ? prefix(`/posts/create?piece=${encodeURIComponent(pieceId)}`)
                : prefix('/posts/create'),
        postsEdit: (postId: string) => prefix(`/posts/${postId}/edit`),
        postsUpdate: (postId: string) => prefix(`/posts/${postId}`),
        postsCategoriesStore: () => prefix('/posts/categories'),
        schedulerStore: () => prefix('/scheduler'),
        schedulerStoreMany: () => prefix('/scheduler/bulk'),
        schedulerMedia: () => prefix('/scheduler/media'),
        schedulerMediaChunkInit: () => prefix('/scheduler/media/chunk/init'),
        schedulerMediaChunk: () => prefix('/scheduler/media/chunk'),
        schedulerMediaChunkComplete: () => prefix('/scheduler/media/chunk/complete'),
        schedulerPost: (postId: string) => prefix(`/scheduler/${postId}`),
        schedulerRetry: (postId: string) => prefix(`/scheduler/${postId}/retry`),
        schedulerFailed: () => prefix('/scheduler?status=failed'),
        schedulerPublishing: () => prefix('/scheduler?status=publishing'),
        billing: () => '/settings/billing',
        connections: () => prefix('/connections'),
        connectionsConnect: (channel: string) => prefix(`/connections/${channel}/connect`),
        connectionsRename: (accountId: string) => prefix(`/connections/${accountId}`),
        connectionsDefault: (accountId: string) => prefix(`/connections/${accountId}/default`),
        connectionsDisconnect: (accountId: string) => prefix(`/connections/${accountId}`),
        adAccounts: () => prefix('/ad-accounts'),
        adConnectionsConnect: (platform: string) => prefix(`/ad-accounts/${platform}/connect`),
        adConnectionsDisconnect: (accountId: string) => prefix(`/ad-accounts/${accountId}`),
        ads: () => prefix('/ads'),
        adsCampaignNew: () => prefix('/ads/campaigns/new'),
        adsCampaignsStore: () => prefix('/ads/campaigns'),
        adsCampaign: (campaignId: string) => prefix(`/ads/campaigns/${campaignId}`),
        adsCampaignLaunch: (campaignId: string) => prefix(`/ads/campaigns/${campaignId}/launch`),
        adsCampaignPause: (campaignId: string) => prefix(`/ads/campaigns/${campaignId}/pause`),
        adsCampaignResume: (campaignId: string) => prefix(`/ads/campaigns/${campaignId}/resume`),
        adsCampaignArchive: (campaignId: string) => prefix(`/ads/campaigns/${campaignId}/archive`),
        adsGroupsStore: (campaignId: string) => prefix(`/ads/campaigns/${campaignId}/groups`),
        adsGroup: (campaignId: string, groupId: string) => prefix(`/ads/campaigns/${campaignId}/groups/${groupId}`),
        adsAdsStore: (campaignId: string, groupId: string) =>
            prefix(`/ads/campaigns/${campaignId}/groups/${groupId}/ads`),
        adsAd: (campaignId: string, groupId: string, adId: string) =>
            prefix(`/ads/campaigns/${campaignId}/groups/${groupId}/ads/${adId}`),
        adsCreatives: () => prefix('/ads/creatives'),
        adsCreativeNew: () => prefix('/ads/creatives/new'),
        adsCreativesStore: () => prefix('/ads/creatives'),
        adsCreative: (creativeId: string) => prefix(`/ads/creatives/${creativeId}`),
        adsAiFixCopy: () => prefix('/ads/ai/fix-copy'),
        adsAudiences: () => prefix('/ads/audiences'),
        adsAudienceNew: () => prefix('/ads/audiences/new'),
        adsAudiencesStore: () => prefix('/ads/audiences'),
        adsAudience: (audienceId: string) => prefix(`/ads/audiences/${audienceId}`),
        analytics: () => prefix('/analytics'),
        insights: () => prefix('/insights'),
        onboarding: () => prefix('/onboarding'),
    };
}
